import React, { type JSX } from 'react';

import { useConfirm } from '@fiji/common/src/hooks';
import moment from 'moment';
import { CustomModal, CustomTransComponent, Loader } from '../../../components';
import { FormHelperText, Grid, List, Stack, Switch, Typography } from '@mui/material';
import { ModalActions } from '../../../components/CustomModal';
import {
    useGetDashboardByIdQuery,
    useUpdateDashboardMutation,
} from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { InfoListItem } from '@brightlayer-ui/react-components';
import CustomIcon from '../../../components/CustomIcon';
import { useParams } from 'react-router-dom';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useAppDispatch } from '@fiji/common/src/app/store';
import { useGetAllWidgetsQuery } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { useTheme } from '@mui/material/styles';
import { resetLoaders, resetLocalDashboard } from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { useTranslation } from 'react-i18next';
import { resetDashboard } from '@fiji/common/src/features/dashboardManagement/commonDashboardSlice';
import LanguageTextField from 'components/FormElements/LanguageTextField';
import { useTextFieldError } from 'hooks';

// ---------------------------------------------------------------------------
// Pure helpers – each < 10 CCN, no component dependencies
// ---------------------------------------------------------------------------

/**
 * Builds the update-dashboard request body.
 * Enabling promotes the dashboard to a SOURCE default (status ACTIVE).
 * When the user opts into a source modal on a DEVICE dashboard, the assignType
 * is flipped to SOURCE and the original sourceId is preserved. CCN 3
 */
function buildBodyClone(
    name: string,
    description: string,
    isEnable: boolean,
    sourceModal: boolean | undefined,
    dashAssignType: string | undefined,
    dashSourceId: string | undefined
): Record<string, unknown> {
    if (isEnable) {
        return { name, description, isEnable: true, status: 'ACTIVE', assignType: 'SOURCE' };
    }
    return {
        name,
        description,
        ...(dashAssignType === 'DEVICE' &&
            sourceModal && {
                assignType: 'SOURCE',
                assignTypeIds: [dashSourceId],
            }),
    };
}

/** Resolves the assignment column label from raw dashboard data. CCN 5 */
function resolveAssignmentLabel(
    isSystem: boolean | undefined,
    assignment: { name?: string; email?: string } | undefined
): string {
    if (isSystem) return 'System';
    const { name = '', email = '' } = assignment ?? {};
    const isValidName = Boolean(name.trim()) && !name.includes('null');
    return isValidName ? name : email || '--';
}

/** Single row in the embedded-widgets list. CCN 7 */
function WidgetListItem({ widget }: { widget: any }): JSX.Element {
    const { name, sourceName, updatedAt, widgetType, id } = widget;
    // Destructure icon config in one chain to avoid repeating ?.icon?.web twice.
    const { family = '', name: iconName = '' } = widgetType?.icon?.web ?? {};
    const ts = updatedAt ?? Date.now();
    return (
        <InfoListItem
            key={id}
            icon={<CustomIcon family={family} iconName={iconName} />}
            title={name}
            subtitle={widgetType?.name}
            divider="full"
            rightComponent={
                <Stack spacing={2} direction={'row'} alignItems={'center'}>
                    <Stack alignItems={'center'} justifyContent={'space-between'}>
                        <Typography variant="body2" className="fw-400">
                            {sourceName ?? <CustomTransComponent translationKey={'COMMON:DEFAULT'} />}
                        </Typography>
                        <Stack direction={'row'}>
                            <Typography variant="body2" fontSize={'14px'}>
                                <span className="black-500 f-14 fw-700">{moment(ts).format('h:mm')}</span>{' '}
                                {moment(ts).format('A')}
                            </Typography>
                            <Typography variant="body2" fontWeight={'400'} fontSize={'14px'} ml={1}>
                                {moment(ts).format('DD/MM/YYYY')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            }
        />
    );
}

type DashboardDetailsSnapshot = {
    name?: string;
    description?: string;
    isEnable?: boolean;
    sourceModal?: boolean;
};

// ---------------------------------------------------------------------------

export const EditDashboardDetails = React.forwardRef((props, ref): JSX.Element => {
    const { t } = useTranslation();
    const theme: any = useTheme();
    // Destructured once; removes repeated `?.palette?.primary` chains in JSX.
    const primary = theme?.palette?.primary;
    const { data: dashboardData, onClick, onCancel, isVisible, onConfirm } = useConfirm(submitHandler);
    const { dashboardId } = useParams();
    const { hasTextError, makeErrorHandler } = useTextFieldError();
    const {
        data: widgets,
        isLoading: widgetLoader,
        isFetching: widgetFetcher,
    }: any = useGetAllWidgetsQuery(
        {
            body: { page: 0, size: 20, filters: { dashboardId: dashboardId ?? dashboardData?.id } },
        },
        { skip: !(dashboardId || dashboardData?.id), refetchOnMountOrArgChange: true }
    );
    const { currentData: dashboard, isFetching }: any = useGetDashboardByIdQuery(dashboardId ?? dashboardData?.id, {
        skip: !(dashboardId || dashboardData?.id),
        refetchOnMountOrArgChange: true,
    });
    // Destructured once; removes repeated `dashboard?.data?.` chains throughout.
    const dashData = dashboard?.data;
    const dispatch = useAppDispatch();
    const [updateDashboard, { isLoading }] = useUpdateDashboardMutation();
    const initializedForDashboardId = React.useRef<string | null>(null);
    const [payload, setPayload] = React.useState<any>({
        name: '',
        description: '',
        dashboardId: dashboardId ?? '',
        isEnable: false,
    });
    const [initialPayload, setInitialPayload] = React.useState<DashboardDetailsSnapshot>({
        name: '',
        description: '',
        isEnable: false,
    });

    const normalizeValue = React.useCallback(
        (value: unknown): string => (typeof value === 'string' ? value.trim() : ''),
        []
    );

    const hasDashboardDetailsChanged = React.useMemo((): boolean => {
        const initialBody = buildBodyClone(
            normalizeValue(initialPayload?.name),
            normalizeValue(initialPayload?.description),
            Boolean(initialPayload?.isEnable),
            Boolean(initialPayload?.sourceModal),
            dashData?.assignType,
            dashData?.sourceId
        );
        const currentBody = buildBodyClone(
            normalizeValue(payload?.name),
            normalizeValue(payload?.description),
            Boolean(payload?.isEnable),
            Boolean(payload?.sourceModal),
            dashData?.assignType,
            dashData?.sourceId
        );

        return JSON.stringify(currentBody) !== JSON.stringify(initialBody);
    }, [payload, initialPayload, normalizeValue, dashData?.assignType, dashData?.sourceId]);

    const isSaveDisabled = React.useMemo((): boolean => {
        const isNameEmpty = !normalizeValue(payload?.name);
        return isNameEmpty || !hasDashboardDetailsChanged || hasTextError || isLoading || isFetching;
    }, [payload?.name, hasDashboardDetailsChanged, hasTextError, isLoading, isFetching, normalizeValue]);

    async function submitHandler(): Promise<void> {
        const bodyClone = buildBodyClone(
            payload?.name,
            payload?.description,
            payload?.isEnable,
            payload?.sourceModal,
            dashData?.assignType,
            dashData?.sourceId
        );
        const { error }: any = await updateDashboard({
            body: bodyClone,
            params: { dashboardId: dashboardId ?? dashboardData?.id },
        });
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('DASHBOARDS:UPDATE_DASHBOARD_DETAILS_MSG', { name: payload?.name }),
                })
            );
            if (dashboardId) {
                dispatch(resetLocalDashboard());
                dispatch(resetLoaders());
            }
            dispatch(resetDashboard());
            onCancel();
        }
    }

    const detailsActions: ModalActions[] = [
        {
            key: 'cancel',
            label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
            close: true,
            variant: 'outlined',
            handleClick: onCancel,
            testId: 'edit-dashboard-cancel-btn',
        },
        {
            key: 'Save',
            label: <CustomTransComponent translationKey={'COMMON:SAVE_LABEL'} />,
            variant: 'contained',
            disabled: isSaveDisabled,
            handleClick: onConfirm,
            testId: 'edit-dashboard-save-btn',
        },
    ];

    React.useEffect(() => {
        if (!isVisible) {
            initializedForDashboardId.current = null;
            return;
        }

        if (!dashData) return;

        const currentDashboardId = String(dashData?.id ?? dashboardId ?? dashboardData?.id ?? '');
        if (initializedForDashboardId.current === currentDashboardId) return;

        const currentPayload = {
            name: dashData?.name,
            description: dashData?.description,
            sourceModal: dashData?.assignType === 'SOURCE',
            ...(dashData?.assignType === 'SOURCE' && dashData?.sourceId && { sourceId: dashData?.sourceId }),
            status: dashData?.status,
            isEnable: dashData?.assignType === 'SOURCE' && dashData?.status === 'ACTIVE',
        };
        setPayload(currentPayload);
        setInitialPayload({
            name: currentPayload?.name,
            description: currentPayload?.description,
            isEnable: currentPayload?.isEnable,
            sourceModal: currentPayload?.sourceModal,
        });
        initializedForDashboardId.current = currentDashboardId;
    }, [isVisible, dashData, dashboardId, dashboardData?.id]);

    const handleModalAction = (action: any, data: any): void => {
        if (action) {
            onClick(data);
            if (data) {
                const currentPayload = {
                    name: data?.name,
                    description: data?.description,
                    status: data?.status,
                    isEnable: data?.isEnable,
                    sourceModal: data?.assignType === 'SOURCE',
                };
                setPayload(currentPayload);
                setInitialPayload({
                    name: currentPayload?.name,
                    description: currentPayload?.description,
                    isEnable: currentPayload?.isEnable,
                    sourceModal: currentPayload?.sourceModal,
                });
            }
            return;
        }
        onCancel();
    };

    const handlePayloadChange = (e: any): void => {
        setPayload((prev: any) => ({ ...prev, [e.target.id]: e.target.value }));
    };

    React.useImperativeHandle(
        ref,
        () => ({
            handleModalAction: (action: any, data: any) => handleModalAction(action, data),
        }),
        []
    );

    const handleKeyDown = (event: any): any => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const cannotEditStatus = (): boolean | undefined =>
        dashData?.assignType === 'DEVICE' ? false : dashData?.status === 'ACTIVE';

    return (
        <CustomModal
            key="#detailsModal"
            isOpen={isVisible}
            actions={detailsActions}
            isLoading={isFetching || isLoading}
            type="secondary"
            header={<CustomTransComponent translationKey={'DASHBOARDS:EDIT_DASHBOARD.DASHBOARD_DETAILS_LABEL'} />}
            {...props}
        >
            <Grid container spacing={2}>
                <Grid size={6}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                            <CustomTransComponent translationKey={'COMMON:MODIFIED'} />
                        </Typography>
                        <Stack direction={'row'}>
                            <Typography variant="body2" fontSize={'14px'}>
                                <span className="black-500 f-14 fw-700">
                                    {moment(dashData?.updatedAt ?? Date.now()).format('h:mm')}
                                </span>{' '}
                                {moment(dashData?.updatedAt ?? Date.now()).format('A')}
                            </Typography>
                            <Typography variant="body2" fontWeight={'400'} fontSize={'14px'} ml={1}>
                                {moment(dashData?.updatedAt ?? Date.now()).format('DD/MM/YYYY')}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mt={2}>
                        <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                            <CustomTransComponent translationKey={'COMMON:DATA_SOURCE'} />
                        </Typography>
                        <Typography
                            variant="body2"
                            fontWeight={'400'}
                            fontSize={'14px'}
                            data-testid="current-data-source-name"
                        >
                            {dashData?.assignType === 'GROUP' ? t('COMMON:GROUP') : dashData?.sourceName ?? '--'}
                        </Typography>
                    </Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mt={2}>
                        <Typography variant="subtitle2" fontWeight={'600'} fontSize={'14px'}>
                            <CustomTransComponent translationKey={'COMMON:ASSIGNMENT'} />
                        </Typography>
                        <Typography variant="body2" fontWeight={'400'} fontSize={'14px'}>
                            {resolveAssignmentLabel(dashData?.isSystemDashboard, dashData?.assignment)}
                        </Typography>
                    </Stack>
                    <Typography variant="body1" mt={2}>
                        <CustomTransComponent translationKey={'DASHBOARDS:DASHBOARD_DETAILS_MODAL.DESCRIPTION'} />
                    </Typography>
                    <LanguageTextField
                        className="margin-top-16"
                        id="name"
                        data-testid="dashboard-name-field"
                        label={<CustomTransComponent translationKey={'COMMON:NAME'} />}
                        defaultValue={'FX30S'}
                        variant="filled"
                        fullWidth
                        inputProps={{ maxLength: 32 }}
                        value={payload?.name}
                        onChange={handlePayloadChange}
                        required
                        handleTextError={makeErrorHandler('name')}
                    />
                    <Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} px={2} mb={2}>
                        <FormHelperText>
                            <CustomTransComponent translationKey={'COMMON:REQUIRED_LABEL'} />
                        </FormHelperText>
                        <FormHelperText>{`${payload?.name?.length ?? 0}/32`}</FormHelperText>
                    </Stack>
                    <LanguageTextField
                        id="description"
                        data-testid="dashboard-description-field"
                        label={<CustomTransComponent translationKey={'COMMON:DESCRIPTION'} />}
                        variant="filled"
                        inputProps={{ maxLength: 256 }}
                        multiline
                        onKeyDown={handleKeyDown}
                        value={payload?.description}
                        onChange={handlePayloadChange}
                        rows={4}
                        fullWidth
                        handleTextError={makeErrorHandler('description')}
                    />
                    <FormHelperText className="text-align-end padding-x-16">{`${
                        payload?.description?.length ?? 0
                    }/256`}</FormHelperText>
                    {dashData?.assignType !== 'GROUP' && (
                        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                            <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                                <CustomTransComponent
                                    translationKey={'DASHBOARDS:DASHBOARD_DETAILS_MODAL.SET_AS_DEFAULT'}
                                    replace={{ sourceName: dashData?.sourceName ?? '--' }}
                                />
                            </Typography>
                            <Switch
                                data-testid="edit-dashboard-default-switch"
                                disabled={cannotEditStatus()}
                                checked={payload?.isEnable}
                                onChange={(): void => {
                                    setPayload((prev: any) => ({
                                        ...prev,
                                        isEnable: !prev?.isEnable,
                                    }));
                                }}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: primary?.main,
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: primary?.main,
                                        },
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': {
                                        color: primary?.[50],
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: primary?.[200],
                                        },
                                    },
                                }}
                            />
                        </Stack>
                    )}
                </Grid>
                <Grid size={6}>
                    <Typography variant="body1" fontWeight={'600'} fontSize={'16px'}>
                        <CustomTransComponent translationKey={'DASHBOARDS:DASHBOARD_DETAILS_MODAL.EMBEDED_WIDGETS'} />
                    </Typography>
                    <Stack p={2} mt={2} className="bg-content-wrapper">
                        <Stack className="bg-white height-390">
                            <List disablePadding className="max-height-650 overflow-y-scroll">
                                {(widgetLoader || widgetFetcher) && <Loader size={10} />}
                                {Boolean(widgets?.data?.records?.length) &&
                                    widgets?.data?.records?.map((widget: any) => (
                                        <WidgetListItem key={widget.id} widget={widget} />
                                    ))}
                            </List>
                        </Stack>
                    </Stack>
                </Grid>
            </Grid>
        </CustomModal>
    );
});
